import { FormApi } from "final-form";
import React, { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import i18next from "src/i18n/config";
import { useDispatch, useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { updateUniversalLicense } from "@dashboard/devices/store";
import { License } from "@dashboard/devices/types";
import LicensesSelectors from "@dashboard/licenses/store/selectors";
import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton } from "@shared/CustomButton";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { TextField } from "@shared/form/TextField";
import { MinimalTable, MinimalTableContainer, MinimalTableHeader, MinimalTableRow } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { TableHeaderRows } from "@shared/table/TableHeader";
import { TableRowCells } from "@shared/table/TableRowCells";
import { TextLink } from "@shared/TextLink";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useLicenses } from "./api/helpers";
import { ActionCell, ActivationCodeCell, ProvisioningCell, TargetGroupCell } from "./components/Cells";
import { getProvisioningsUsingCode, RemoveUniversalStatusDialog } from "./components/RemoveUniversalStatusDialog";

const useStyles = makeStyles()((theme) => ({
    errorAlertText: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    inputBox: {
        marginBottom: theme.spacing(4),
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(2),
    },
    input: {
        width: 300,
    },
    targetGroup: {
        fontsize: 16,
        fontWeight: 600,
    },
    steps: {
        marginBlock: theme.spacing(1),
        paddingLeft: theme.spacing(5),
    },
    loadingAlert: {
        marginBottom: theme.spacing(2),
    },
    tableBody: {
        "& > tr:last-child td": {
            paddingBottom: theme.spacing(3),
        },
    },
}));

type FormValues = { activationCode: string };
const columnHelper = createColumnHelper<License>();

const UniversalLicensesPage = () => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["common", "modals", "pages"]);
    const navigate = useNavigate();

    const licenses = useLicenses();
    const universalLicenses = React.useMemo(() => licenses.filter((license) => !!license.universalGroupName), [licenses]);
    const licenseStatus = useSelector(LicensesSelectors.selectLicensesStatus);
    const provisionings = useSelector(selectProvisionings);
    const isSubscriptionExpired = useCurrentSubscriptionExpired();
    const [licenseToDelete, setLicenseToDelete] = useState<License | null>(null);

    const isLoadingLicenses = licenseStatus === "pending" || licenseStatus === "updating";

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const license = licenses.find((l) => l.activationCode === values.activationCode);
        if (license?.id) {
            await dispatch(updateUniversalLicense({ id: license.id, groupId: license.groupId }))
                .unwrap()
                .then(() => showSuccessToast(t("pages:universalLicensePage.toast.targetGroupChangedSuccessfully")))
                .catch(({ message = t("pages:universalLicensePage.toast.targetGroupChangeFailed") }) => showErrorToast(message))
                .finally(() => form.restart());
        }
    };

    const validate = (values: FormValues) => {
        const licenseExists = licenses.some((l) => l.activationCode === values.activationCode);
        const activationCodeExists = universalLicenses.some((l) => l.activationCode === values.activationCode);
        const idNotMatchActivationCode = !licenses.find((l) => l.activationCode === values.activationCode)?.id;

        if (!values.activationCode) {
            return { activationCode: t("pages:universalLicensePage.input.activationCode.validation.empty") };
        }
        if (!licenseExists) {
            return { activationCode: t("pages:universalLicensePage.input.activationCode.validation.exist") };
        }
        if (activationCodeExists) {
            return { activationCode: t("pages:universalLicensePage.input.activationCode.validation.alreadyAdded") };
        }
        if (idNotMatchActivationCode) {
            return { activationCode: t("pages:universalLicensePage.input.activationCode.validation.idNotFound") };
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor("activationCode", {
                id: "activationCode",
                header: t("pages:universalLicensePage.table.header.licenseCode"),
                cell: (info) => <ActivationCodeCell license={info.row.original} />,
                enableSorting: false,
            }),
            columnHelper.display({
                id: "targetGroup",
                header: () => (
                    <Tooltip placement="top" title={t("pages:universalLicensePage.tooltip.targetGroup")}>
                        <span className={classes.targetGroup}>{t("pages:universalLicensePage.table.header.targetGroup")}</span>
                    </Tooltip>
                ),
                cell: (info) => <TargetGroupCell license={info.row.original} />,
                enableSorting: false,
            }),
            columnHelper.display({
                id: "provisionings",
                header: t("pages:universalLicensePage.table.header.provisioning"),
                cell: (info) => (
                    <ProvisioningCell
                        provisionings={getProvisioningsUsingCode(provisionings, info.row.original.activationCode)}
                    />
                ),
                enableSorting: false,
            }),
            columnHelper.display({
                id: "actions",
                header: "",
                cell: (info) => <ActionCell license={info.row.original} onDelete={setLicenseToDelete} />,
                enableSorting: false,
            }),
        ],
        [t, classes.targetGroup, provisionings],
    );

    const table = useReactTable({
        data: universalLicenses,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <PageContainer>
            <ExpiredWrapper>
                <CustomAlert severity="info">
                    <Trans
                        ns="pages"
                        i18nKey="universalLicensePage.alert.howItWorks"
                        components={{
                            bold: <strong />,
                            list: <ol className={classes.steps} />,
                            item: <li />,
                            // Not called `link`: <Trans> treats that as a void element and leaves the anchor empty.
                            provisioningLink: (
                                <TextLink
                                    component="button"
                                    type="button"
                                    tooltip={t("pages:universalLicensePage.tooltip.openProvisioning")}
                                    onClick={() => navigate("/dashboard/provisioning")}
                                />
                            ),
                        }}
                    />
                </CustomAlert>
                <CustomAlert className={classes.errorAlertText} severity="error">
                    {t("pages:universalLicensePage.alert.secret")}
                </CustomAlert>
                <Form<FormValues>
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={{ activationCode: undefined }}
                    render={({ handleSubmit, form, submitting }) => (
                        <form onSubmit={handleSubmit}>
                            <div className={classes.inputBox}>
                                <TextField
                                    name="activationCode"
                                    label={t("pages:universalLicensePage.table.header.licenseCode")}
                                    fullWidth
                                />
                                <PrimaryButton
                                    onClick={form.submit}
                                    loading={submitting}
                                    disabled={isSubscriptionExpired}
                                    tooltipProps={{
                                        title: t("pages:universalLicensePage.tooltip.subscriptionExpired"),
                                        hide: !isSubscriptionExpired,
                                    }}
                                >
                                    {t("common:button.add")}
                                </PrimaryButton>
                            </div>
                        </form>
                    )}
                />
                {isLoadingLicenses ? (
                    <CustomAlert className={classes.loadingAlert} severity="info">
                        {t("pages:universalLicensePage.loadingMore")}
                    </CustomAlert>
                ) : null}
                <MinimalTableContainer>
                    <MinimalTable>
                        <MinimalTableHeader>
                            <TableHeaderRows headerGroups={table.getHeaderGroups()} />
                        </MinimalTableHeader>
                        <TableBody
                            table={table}
                            className={classes.tableBody}
                            noDataMessage={t("pages:universalLicensePage.table.noLicenses")}
                        >
                            {table.getRowModel().rows.map((row) => (
                                <MinimalTableRow key={row.id}>
                                    <TableRowCells row={row} />
                                </MinimalTableRow>
                            ))}
                        </TableBody>
                    </MinimalTable>
                </MinimalTableContainer>
            </ExpiredWrapper>
            <RemoveUniversalStatusDialog license={licenseToDelete} onClose={() => setLicenseToDelete(null)} />
        </PageContainer>
    );
};

export default withMetaDescription(UniversalLicensesPage, {
    title: i18next.t("pages:universalLicensePage.metaTitle"),
});
